import type { CSSProperties, ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigation } from "react-router";
import {
  Avatar,
  AvatarFallback,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  Wordmark,
  type IconName,
} from "@diametral/design-system/react";

import { useRequests } from "../api/requests";
import { config } from "../lib/config";
import { useCurrentUser } from "../lib/currentUser";
import { initials } from "../lib/format";
import { logout } from "../lib/keycloak";
import { LANGUAGES, useTranslation } from "../lib/i18n";
import { useTheme, type ThemeMode } from "../lib/theme";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Active on this exact path only (otherwise: the path and everything under it). */
  exact?: boolean;
  badge?: number;
  /** Spoken with the count, e.g. "missing reports". */
  badgeLabel?: string;
}

function isActive(pathname: string, item: NavItem): boolean {
  if (item.exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function NavGroup({ label, items, pathname }: { label: string; items: NavItem[]; pathname: string }) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.to}>
              <SidebarMenuButton
                className="ada-nav-link"
                isActive={isActive(pathname, item)}
                render={<NavLink to={item.to} end={item.exact} />}
              >
                <Icon name={item.icon} aria-hidden="true" />
                <span>{item.label}</span>
              </SidebarMenuButton>
              {item.badge ? (
                <SidebarMenuBadge title={`${item.badge} ${item.badgeLabel ?? ""}`.trim()}>
                  {item.badge}
                  <span className="sr-only"> {item.badgeLabel}</span>
                </SidebarMenuBadge>
              ) : null}
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

const THEMES: Array<{ value: ThemeMode; label: string; icon: IconName }> = [
  { value: "light", label: "Light", icon: "sun" },
  { value: "dark", label: "Dark", icon: "moon" },
  { value: "system", label: "Auto", icon: "settings" },
];

/** One row in the footer: who is signed in. Theme, language and sign-out live in its menu, out of
 * the way of the navigation. */
function AccountMenu() {
  const { t, i18n } = useTranslation();
  const me = useCurrentUser();
  const { mode, setMode } = useTheme();
  const language = i18n.resolvedLanguage === "fr" ? "fr" : "en";
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<SidebarMenuButton size="lg" className="ada-account" aria-label={t("Account and preferences")} />}
          >
            <Avatar size="sm">
              <AvatarFallback>{initials(me.name)}</AvatarFallback>
            </Avatar>
            <span className="ada-account__who">
              <span className="ada-account__name">{me.name}</span>
              {me.email ? <span className="ada-account__email">{me.email}</span> : null}
            </span>
            <Icon name="more-horizontal" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" sideOffset={6} className="ada-account-menu">
            <DropdownMenuGroup>
              <DropdownMenuLabel>{t("Interface theme")}</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={mode} onValueChange={(value) => setMode(value as ThemeMode)}>
                {THEMES.map((theme) => (
                  <DropdownMenuRadioItem key={theme.value} value={theme.value} closeOnClick={false}>
                    {t(theme.label)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel>{t("Language")}</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={language} onValueChange={(code) => void i18n.changeLanguage(String(code))}>
                {LANGUAGES.map((l) => (
                  <DropdownMenuRadioItem key={l.code} value={l.code}>
                    <span lang={l.code}>{l.label}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <Icon name="log-out" aria-hidden="true" /> {t("Sign out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** The Diametral mark and the product name; the environment is shown outside production only. */
function Brand() {
  return (
    <div className="ada-brand">
      <Wordmark variant="horizontal" label="Diametral" />
      <span className="ada-brand__sep" aria-hidden="true" />
      <span className="ada-brand__app">ada</span>
      {config.environment !== "prod" ? (
        <Badge variant="outline" className="ada-brand__env">
          {config.environment}
        </Badge>
      ) : null}
    </div>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const { pathname } = useLocation();
  const loading = useNavigation().state === "loading";
  const pending = useRequests({ status: "pending" }, me.is_dar);

  const main: NavItem[] = [
    { to: "/projects", label: t("Projects"), icon: "folder" },
    { to: "/my-requests", label: t("My requests"), icon: "list" },
    {
      to: "/reports",
      label: t("My report"),
      icon: "edit",
      exact: true,
      badge: me.missing_reports.length,
      badgeLabel: t("missing reports"),
    },
    { to: "/reports/team", label: t("Team reports"), icon: "users" },
    ...(me.person ? [{ to: `/people/${me.person.id}`, label: t("My year"), icon: "calendar" as IconName }] : []),
    ...(me.coachees > 0 ? [{ to: "/my-team", label: t("My team"), icon: "users" as IconName }] : []),
    { to: "/integrations", label: t("Integrations"), icon: "external-link" },
  ];
  const steering: NavItem[] = [
    { to: "/dashboard", label: t("Dashboard"), icon: "grid" },
    { to: "/ipc", label: t("Committee planning"), icon: "calendar", exact: true },
    { to: "/ipc/minutes", label: t("Minutes and actions"), icon: "check" },
    { to: "/resources", label: t("Resources"), icon: "users" },
  ];
  const admin: NavItem[] = [
    {
      to: "/admin/requests",
      label: t("Requests"),
      icon: "bell",
      badge: pending.data?.length ?? 0,
      badgeLabel: t("pending requests"),
    },
    { to: "/admin/projects", label: t("Projects"), icon: "grid" },
    { to: "/admin/people", label: t("People"), icon: "user" },
    { to: "/admin/reports", label: t("Report compliance"), icon: "check" },
    { to: "/admin/settings", label: t("Settings"), icon: "settings" },
  ];

  return (
    <SidebarProvider className="ada-shell" style={{ minHeight: "100svh", "--sidebar-width": "15rem" } as CSSProperties}>
      {loading ? <div className="ada-nav-progress" aria-hidden="true" /> : null}
      <Sidebar>
        <SidebarHeader>
          <Brand />
        </SidebarHeader>
        <SidebarContent>
          <NavGroup label={t("My space")} items={main} pathname={pathname} />
          {me.is_dar ? (
            <>
              <SidebarSeparator />
              <NavGroup label={t("Steering")} items={steering} pathname={pathname} />
              <SidebarSeparator />
              <NavGroup label={t("Administration")} items={admin} pathname={pathname} />
            </>
          ) : null}
        </SidebarContent>
        <SidebarFooter>
          <AccountMenu />
        </SidebarFooter>
      </Sidebar>
      <SidebarInset className="ada-inset">
        {/* Phone width only: the sidebar is a sheet there and needs a button to open it. */}
        <header className="ada-topbar">
          <SidebarTrigger aria-label={t("Show or hide navigation")} />
          <Brand />
        </header>
        {children ?? <Outlet />}
      </SidebarInset>
    </SidebarProvider>
  );
}
